// PDI — two views, toggled at the top:
//   "holds"     → only the open HOLD rows across vessels (the triage view)
//   "full list" → the WHOLE PDI sheet straight from Smartsheet, every row + column, like the
//                 service page. status/comments are editable inline and push back to Smartsheet.
// Each boat also shows how far its loading checklist has got, linking to it: PDI items that match a
// loading item sync both ways with Loading, so a Status edit here moves the Loading item too. Both pages
// refresh on their own. ?view=full&sheet=&sap= opens the full list on one boat (the Loading page links here).

import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LOADING_ITEMS, loadingProgressBySap, type LoadingProgress, type PdiItem, type PdiVessel } from '@technohull/shared';
import {
  useLoading,
  usePdi,
  usePdiGridWrite,
  usePdiSheetGrid,
  usePdiSheets,
  usePdiStatus,
  usePdiWrite,
  type SheetGridColumn,
  type SheetGridRow,
} from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { useCoopView } from '../collab/CollabContext.js';
import {
  Chip, ChipGroup, EmptyState, ErrorState, Fact, FactEnd, FactSentence, FilterBar, FilterToggle, InlineError, LoadingState, PageHeader,
  ProgressBar, SearchInput, Segmented, SyncLabel, useConfirm,
} from '../components/ui.js';
import { useCellHistoryHover } from '../components/CellHistory.js';
import { PdiAddBoat } from '../components/PdiAddBoat.js';
import { IconCheck, IconChevronRight, IconPaperclip, IconPencil } from '../components/icons.js';
import { SortMark } from '../components/SortMark.js';
import { statusPillKind, useDateFormatter } from '../lib/format.js';
import { commentStamp } from '../lib/dates.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { pdiChecked, pdiLooseHolds, pdiOnHold, pdiPassed, pdiProgress, pdiSection, type PdiSectionView } from '../lib/pdi.js';
import { fold, hasText } from '../lib/search.js';
import { sortRows, useTableSort } from '../lib/tableSort.js';

type View = 'holds' | 'full';

const cellStr = (v: string | boolean | undefined) => (typeof v === 'string' ? v : v == null ? '' : v === true ? '✓' : '');

// status/symbol option name → a dot in the one status vocabulary (RYG balls + PDI's Yes/No/Hold): Yes
// ok green, Hold the journey strip's amber hatch, No neutral (as on the Overview grid). Tokens, so the
// dots follow light, dark and every colour profile.
const HOLD_HATCH = 'repeating-linear-gradient(135deg, var(--warn-fill) 0 2px, var(--warn-hatch) 2px 3.5px)';
const DOT: Record<string, string> = {
  green: 'var(--ok-fill)', yellow: 'var(--warn-fill)', red: 'var(--err-fill)', blue: 'var(--info-fill)', gray: 'var(--text-3)', grey: 'var(--text-3)',
  yes: 'var(--ok-fill)', no: 'var(--text-3)', hold: HOLD_HATCH,
};
const statusDot = (v: string): string | null => (v ? DOT[v.trim().toLowerCase()] ?? null : null);

// append a dated line to a comment, matching the desktop app: "[D mon YYYY] text". The stamp is written INTO
// the shared comment, so it is one fixed form for everyone, never a viewer's date preference.
function stampComment(existing: string, text: string): string {
  const ts = commentStamp();
  return `${existing ? existing + '\n' : ''}[${ts}] ${text}`;
}

// ── the boat's loading checklist, as a chip that opens it ─────────────────────────
/** a boat with no Loading rows yet: everything pending. */
const NO_LOADING: LoadingProgress = { loadedN: 0, na: 0, applicable: LOADING_ITEMS.length, done: false };

/** "Loading 12/26": loaded of the items that apply (N/A ones don't count), in the Loading page's colours
 *  (done green, started amber, not started neutral). A link, so a click on it never folds the card. */
function LoadingChip({ sap, p }: { sap: string; p: LoadingProgress }) {
  const text = p.applicable === 0 ? 'Loading: all N/A' : `Loading ${p.loadedN}/${p.applicable}`;
  const title = `Open the loading checklist: ${p.loadedN} of ${p.applicable} loaded${p.na ? `, ${p.na} N/A` : ''}`;
  return (
    <Link
      className={`pill pdi-load ${p.done ? 'ok' : p.loadedN ? 'warn' : ''}`}
      to={`/loading?sap=${encodeURIComponent(sap)}`}
      title={title}
      aria-label={`${title} (${sap})`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* the text clips, not the chip, so the chip's touch hit area (::after) isn't cut off */}
      <span className="pdi-load-txt">{text}</span><IconChevronRight size={12} />
    </Link>
  );
}

// ── holds view ────────────────────────────────────────────────────────────────
// Each boat on hold opens onto its checklist: the sections that still need something, and in each the
// items not checked yet, with Yes / No / Hold one tap away (saved straight away, as the full list does).
// What is blocking shows first: a section with an item on hold, or only a few items left, starts open; a
// section nobody has started stays a single line until it is opened.

type StatusOption = { value: string; label: string; title?: string };
const ITEM_OPTIONS: StatusOption[] = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No', title: 'Doesn’t apply to this boat (counts as checked)' },
  { value: 'Hold', label: 'Hold' },
];
const BOAT_OPTIONS: StatusOption[] = [{ value: 'Hold', label: 'Hold' }, { value: 'Yes', label: 'Yes' }];
/** a section opens by itself when it holds something, or is under way with this many items left or fewer */
const FEW_LEFT = 4;
const statusKind = (s: string) => (s === 'Yes' ? 'ok' : s === 'Hold' ? 'hold' : s === 'No' ? 'no' : 'none');

/** a row's Status as one tap: a radio group in the shared .seg look that wears its value (Yes ok, Hold
 *  amber, No neutral). Picking the value it already has sends nothing. */
function StatusSeg({ value, options, label, onPick }: { value: string; options: StatusOption[]; label: string; onPick: (v: string) => void }) {
  const sel = options.findIndex((o) => o.value === value);
  const stop = sel < 0 ? 0 : sel;
  return (
    <div
      className={`seg pdi-seg is-${statusKind(value)}`}
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const j = (stop + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        (e.currentTarget.children[j] as HTMLElement | undefined)?.focus();
      }}
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          tabIndex={i === stop ? 0 : -1}
          title={o.title}
          className={`seg-btn${value === o.value ? ' on' : ''}`}
          onClick={() => { if (o.value !== value) onPick(o.value); }}
        >{o.label}</button>
      ))}
    </div>
  );
}

/** a read-only status, for people who can't edit PDI */
const StatusPill = ({ value }: { value: string }) => (
  <span className={`pill ${statusPillKind(value)}`}>{value || 'Not checked'}</span>
);

/** the "Note" toggle beside a row's status */
function NoteToggle({ open, onClick, label, iconOnly }: { open: boolean; onClick: () => void; label: string; iconOnly?: boolean }) {
  return (
    <button
      type="button"
      className={`btn btn-sm btn-ghost pdi-note-btn${iconOnly ? ' is-icon' : ''}${open ? ' on' : ''}`}
      aria-expanded={open}
      aria-label={`Add a note to ${label}`}
      title={iconOnly ? 'Add a note' : undefined}
      onClick={onClick}
    >
      <IconPencil size={13} />{!iconOnly && <span>Note</span>}
    </button>
  );
}

/** what the row's Comments hold, then (when open) a field that appends a dated line to them */
function PdiNote({ v, rowId, comment, label, open, onClose }: {
  v: PdiVessel; rowId: number; comment: string; label: string; open: boolean; onClose: () => void;
}) {
  const write = usePdiWrite();
  const [text, setText] = useState('');
  const save = () => {
    const t = text.trim();
    if (!t || write.isPending) return;
    write.mutate(
      { sheetId: v.sheetId, rowId, cells: [{ columnId: v.colComments, value: stampComment(comment, t) }] },
      { onSuccess: () => { setText(''); onClose(); } },
    );
  };
  return (
    <>
      {comment && <div className="pdi-note">{comment}</div>}
      {open && (
        <div className="pdi-note-add">
          <input
            autoFocus
            className="field"
            placeholder="What’s missing or wrong…"
            aria-label={`Note for ${label}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onClose(); }}
          />
          <button type="button" className="btn btn-sm btn-primary" disabled={!text.trim() || write.isPending} onClick={save}>
            {write.isPending ? 'Saving…' : 'Save note'}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      )}
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
    </>
  );
}

/** one checklist item: its name (led by the hold mark when on hold), its status and its note */
function ItemRow({ v, item, canWrite, onStatus }: {
  v: PdiVessel; item: Pick<PdiItem, 'name' | 'status' | 'comment' | 'rowId'>; canWrite: boolean; onStatus: (rowId: number, s: string) => void;
}) {
  const [noting, setNoting] = useState(false);
  return (
    <li className={`pdi-item is-${statusKind(item.status)}`}>
      <div className="pdi-item-main">
        <span className="pdi-item-name">
          {item.status === 'Hold' && <i className="pdi-hatch" aria-hidden="true" />}
          {item.name}
        </span>
        {canWrite ? (
          <span className="pdi-item-acts">
            <StatusSeg value={item.status} options={ITEM_OPTIONS} label={`Status - ${item.name}`} onPick={(s) => onStatus(item.rowId, s)} />
            <NoteToggle iconOnly open={noting} onClick={() => setNoting((o) => !o)} label={item.name} />
          </span>
        ) : (
          <StatusPill value={item.status} />
        )}
      </div>
      <PdiNote v={v} rowId={item.rowId} comment={item.comment} label={item.name} open={noting} onClose={() => setNoting(false)} />
    </li>
  );
}

/** a checklist section: its name and count, its hold, its note, and its items left to check (the checked
 *  ones a tap away, so a slip can be put right) */
function SectionBlock({ v, s, canWrite, keep, onStatus, onBulk }: {
  v: PdiVessel;
  s: PdiSectionView;
  canWrite: boolean;
  /** rows changed on this card: they stay in view after they are checked */
  keep: ReadonlySet<number>;
  onStatus: (rowId: number, status: string) => void;
  onBulk: (s: PdiSectionView) => void;
}) {
  const [open, setOpen] = useState(() => s.held > 0 || (s.checked > 0 && s.open.length > 0 && s.open.length <= FEW_LEFT));
  const [all, setAll] = useState(false);
  const [noting, setNoting] = useState(false);
  const items = all ? s.cat.items : s.cat.items.filter((it) => !pdiChecked(it.status) || keep.has(it.rowId));
  const hidden = s.cat.items.length - items.length;
  const blank = s.open.filter((it) => !it.status).length;
  const state = s.done ? 'Done' : s.checked === 0 && !s.held ? 'Not started' : `${s.open.length} to check`;
  return (
    <li className={`pdi-sec${s.onHold ? ' is-hold' : ''}${s.done ? ' is-done' : ''}`}>
      <div className="pdi-sec-hd">
        <button type="button" className={`pdi-sec-tog${open ? ' is-open' : ''}`} aria-expanded={open} onClick={() => { setOpen((o) => !o); setNoting(false); }}>
          <IconChevronRight size={14} />
          <span className="pdi-sec-name">{s.cat.name}</span>
        </button>
        <span className="pdi-sec-meta">
          <span className="pdi-sec-count">{s.checked}/{s.total}</span>
          <span className="pdi-sec-state">{state}</span>
          {(s.onHold || s.held > 0) && (
            <span className="pdi-sec-held">
              <i className="pdi-hatch" aria-hidden="true" />
              {s.held > 0 ? `${s.held} item${s.held === 1 ? '' : 's'} on hold` : 'On hold'}
            </span>
          )}
        </span>
        {canWrite && (
          <span className="pdi-sec-acts">
            {s.onHold && s.open.length === 0 && (
              <button type="button" className="btn btn-sm" onClick={() => onStatus(s.cat.rowId, 'Yes')} title="Every item is checked: set the section to Yes">Clear the hold</button>
            )}
            {open && blank > 1 && (
              <button type="button" className="btn btn-sm" onClick={() => onBulk(s)} title="Set every item not checked yet to Yes; items on hold stay on hold">All unchecked to Yes</button>
            )}
            {open && <NoteToggle open={noting} onClick={() => setNoting((o) => !o)} label={s.cat.name} />}
          </span>
        )}
      </div>
      <PdiNote v={v} rowId={s.cat.rowId} comment={s.cat.comment} label={s.cat.name} open={noting} onClose={() => setNoting(false)} />
      {open && (
        <>
          {items.length > 0 && (
            <ul className="pdi-items">
              {items.map((it) => <ItemRow key={it.rowId} v={v} item={it} canWrite={canWrite} onStatus={onStatus} />)}
            </ul>
          )}
          {(hidden > 0 || all) && s.checked > 0 && (
            <button type="button" className="pdi-more" onClick={() => setAll((a) => !a)}>
              {all ? 'Hide the checked items' : `Show the ${hidden} checked`}
            </button>
          )}
        </>
      )}
    </li>
  );
}

function VesselCard({ v, canWrite, load }: { v: PdiVessel; canWrite: boolean; load: LoadingProgress | null }) {
  const write = usePdiStatus();
  const [confirmNode, ask] = useConfirm();
  const [open, setOpen] = useState(v.holds.length > 0);
  const [noting, setNoting] = useState(false);
  const [showDone, setShowDone] = useState(false);
  // the rows changed here stay in view, so a tap never makes the row it was on jump away
  const [keep, setKeep] = useState<ReadonlySet<number>>(() => new Set());
  const holdCount = v.holds.length;
  const prog = pdiProgress(v);
  const sections = useMemo(() => v.categories.map(pdiSection), [v.categories]);
  const loose = useMemo(() => pdiLooseHolds(v), [v]);
  const shown = showDone ? sections : sections.filter((s) => !s.done || keep.has(s.cat.rowId) || s.cat.items.some((it) => keep.has(it.rowId)));
  const doneHidden = sections.filter((s) => !shown.includes(s));

  const setStatus = (rowId: number, status: string) => {
    setKeep((k) => (k.has(rowId) ? k : new Set(k).add(rowId)));
    write.mutate({ sheetId: v.sheetId, rowId, columnId: v.colStatus, status });
  };
  const bulk = async (s: PdiSectionView) => {
    const rows = s.open.filter((it) => !it.status);
    const ok = await ask({
      title: `Set ${rows.length} items to Yes?`,
      message: `Every item of “${s.cat.name}” not checked yet on ${v.name} becomes Yes in Smartsheet. Items on hold stay on hold.`,
      confirmLabel: `Set ${rows.length} to Yes`,
    });
    if (ok) for (const it of rows) setStatus(it.rowId, 'Yes');
  };

  return (
    <div className={`pdi-card ${holdCount ? 'has-holds' : ''}`}>
      <div className="pdi-card-hd" onClick={() => setOpen((o) => !o)}>
        <button
          type="button"
          className={`pdi-chevron${open ? ' is-open' : ''}`}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${v.sap} ${v.name}`}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        ><IconChevronRight size={14} /></button>
        <span className="pdi-sap">{v.sap}</span>
        {/* phones fold the sheet label into the name line as trailing meta (.pdi-sheet-inline) */}
        <span className="pdi-name">{v.name}<span className="pdi-sheet-inline"> · {v.sheet}</span></span>
        <span className="pdi-sheet">{v.sheet}</span>
        <span className="pdi-spacer" />
        <span className="pdi-card-meta">
          {prog.total > 0 && (
            <span className="pdi-prog" title={`${prog.checked} of ${prog.total} checks done`}>
              <ProgressBar pct={(prog.checked / prog.total) * 100} done={prog.checked === prog.total} />
              <span className="pdi-prog-n">{prog.checked}/{prog.total} checked</span>
            </span>
          )}
          {load && <LoadingChip sap={v.sap} p={load} />}
        </span>
        {/* the hold chip: amber with the journey strip's hatched hold mark; a clear vessel is the ok chip */}
        <span className={`pdi-badge ${holdCount ? 'has-holds' : 'no-holds'}`}>
          {holdCount
            ? <><i className="pdi-hatch" aria-hidden="true" />{holdCount} {holdCount > 1 ? 'holds' : 'hold'}</>
            : <><IconCheck size={12} />No holds</>}
        </span>
      </div>
      {open && (
        <div className="pdi-card-bd">
          <div className="pdi-boat">
            <span className="pdi-boat-lbl">Boat</span>
            {canWrite ? (
              <StatusSeg value={v.status} options={BOAT_OPTIONS} label={`PDI status of ${v.sap} ${v.name}`} onPick={(s) => setStatus(v.rowId, s)} />
            ) : (
              <StatusPill value={v.status} />
            )}
            {canWrite && <NoteToggle open={noting} onClick={() => setNoting((o) => !o)} label={v.name} />}
          </div>
          <PdiNote v={v} rowId={v.rowId} comment={v.comment} label={v.name} open={noting} onClose={() => setNoting(false)} />
          {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
          {(shown.length > 0 || loose.length > 0) ? (
            <ol className="pdi-secs">
              {shown.map((s) => <SectionBlock key={s.cat.rowId} v={v} s={s} canWrite={canWrite} keep={keep} onStatus={setStatus} onBulk={(x) => void bulk(x)} />)}
              {loose.length > 0 && (
                <li className="pdi-sec is-hold">
                  <div className="pdi-sec-hd"><span className="pdi-sec-name pdi-sec-loose">Other holds</span></div>
                  <ul className="pdi-items">
                    {loose.map((h) => <ItemRow key={h.rowId} v={v} item={{ name: h.category, status: h.status, comment: h.comment, rowId: h.rowId }} canWrite={canWrite} onStatus={setStatus} />)}
                  </ul>
                </li>
              )}
            </ol>
          ) : sections.length === 0 ? (
            <p className="muted pdi-alldone">No checklist under this boat on the sheet.</p>
          ) : null}
          {doneHidden.length > 0 && (
            <p className="pdi-done-line">
              <IconCheck size={13} />
              <span>
                {doneHidden.length === sections.length ? 'All' : doneHidden.length} section{doneHidden.length === 1 ? '' : 's'} done: {doneHidden.map((s) => s.cat.name).join(', ')}.
              </span>
              <button type="button" className="pdi-more" onClick={() => setShowDone(true)}>Show</button>
            </p>
          )}
          {showDone && sections.some((s) => s.done) && (
            <button type="button" className="pdi-more" onClick={() => setShowDone(false)}>Hide the finished sections</button>
          )}
        </div>
      )}
      {confirmNode}
    </div>
  );
}

// ── full list view — the raw Smartsheet sheet, rendered like the Service page ─────
// the full list's sort columns: the name (the primary column) or another column by id
type SortKey = 'name' | number;

/** the full list's Comments cell, in the Holds view's append pattern: what is there already, read-only with
 *  its line breaks, plus an "Append…" field that adds a dated line. It never rewrites the old text — a plain
 *  text input strips line breaks, so a focus + blur used to write a flattened comment back to Smartsheet. */
function PdiCommentCell({ sheetId, rowId, col, text, label }: { sheetId: string | null; rowId: number; col: SheetGridColumn; text: string; label: string }) {
  const write = usePdiGridWrite(sheetId);
  const [add, setAdd] = useState('');
  // some sheets make Comments a dropdown (ΛΕΙΠΕΙ, UPDATE, …): its values are offered as you type, and a
  // dated note the Holds view added still shows instead of an empty dropdown
  const listId = `pdi-com-${rowId}`;
  const save = () => {
    const t = add.trim();
    if (!t) return;
    write.mutate({ rowId, columnId: col.id, value: stampComment(text, t) }, { onSuccess: () => setAdd('') });
  };
  return (
    <div className="pdi-com">
      {text && <div className="pdi-com-text">{text}</div>}
      <div className="pdi-com-add">
        <input
          className="field"
          placeholder="Append…"
          aria-label={`Append to ${col.title}${label ? ` for ${label}` : ''}`}
          value={add}
          list={col.options.length ? listId : undefined}
          onChange={(e) => setAdd(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        />
        {col.options.length > 0 && <datalist id={listId}>{col.options.map((o) => <option key={o} value={o} />)}</datalist>}
        <button type="button" className="btn btn-sm" disabled={!add.trim() || write.isPending} onClick={save}>
          {write.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
    </div>
  );
}

/** a boat to open the full list on: its SAP, its PDI sheet (which may be an earlier season's) and a label
 *  for that sheet when it isn't one of this season's. */
interface PdiFocus { sap: string; sheetId: string | null; label: string | null }

function FullList({ canWrite, q, setQ, focus, loadBySap }: {
  canWrite: boolean;
  q: string;
  setQ: (v: string) => void;
  focus: PdiFocus | null;
  /** each boat's loading progress, or null when the viewer can't see Loading */
  loadBySap: Map<string, LoadingProgress> | null;
}) {
  const df = useDateFormatter();
  const sheets = usePdiSheets();
  const [sheetId, setSheetId] = useState<string | null>(focus?.sheetId ?? null);
  // auto-follow the season: if the picked sheet isn't in the active season's list (its PDI sheets may
  // differ), fall back to that season's first sheet rather than showing a stale selection. The sheet a
  // link from Loading asked for stays, though: a boat can still be in last season's PDI.
  const sheetList = sheets.data?.sheets ?? [];
  const focusSheet = focus?.sheetId ?? null;
  const extraSheet = focusSheet && sheets.data && !sheetList.some((s) => s.id === focusSheet) ? focusSheet : null;
  const active = (sheetId && (sheetList.some((s) => s.id === sheetId) || sheetId === focusSheet)) ? sheetId : (sheetList[0]?.id ?? null);
  const grid = usePdiSheetGrid(active);
  const { onEnter: histEnter, onLeave: histLeave, bindPress: histPress, popover: histPopover } = useCellHistoryHover(grid.data?.sheetId);
  const write = usePdiGridWrite(active);
  // phone: the name column is pinned and Status / Comments follow it; touch: the whole name cell of a
  // branch opens / closes it
  const isPhone = useIsPhone();
  const coarse = useIsCoarse();

  const [statusSel, setStatusSel] = useState('ALL');
  const [showAllCols, setShowAllCols] = useState(false);
  // starts in the sheet's own order (the tree); a header click cycles A→Z, Z→A, back to the tree
  const { sort: pickedSort, setSort, dirOf, th: sortTh } = useTableSort<SortKey>('pdi', null);

  // share the full-list filters over a co-op session.
  useCoopView(
    { pdiSheet: active, pdiStatus: statusSel, pdiAllCols: showAllCols, pdiSortKey: pickedSort?.key ?? null, pdiSortDir: pickedSort?.dir === 'desc' ? -1 : 1 },
    (v) => {
      if (typeof v.pdiSheet === 'string') setSheetId(v.pdiSheet);
      if (typeof v.pdiStatus === 'string') setStatusSel(v.pdiStatus);
      if (typeof v.pdiAllCols === 'boolean') setShowAllCols(v.pdiAllCols);
      if ('pdiSortKey' in v) {
        const key = v.pdiSortKey as SortKey | null | undefined;
        setSort(key == null ? null : { key, dir: v.pdiSortDir === -1 ? 'desc' : 'asc' });
      }
    },
  );

  const columns = grid.data?.columns ?? [];
  const primaryCol = useMemo(() => columns.find((c) => c.primary) ?? columns[0], [columns]);
  const otherCols = useMemo(() => columns.filter((c) => c.id !== primaryCol?.id), [columns, primaryCol]);
  // the sort is kept per tab, so a column picked on another PDI sheet could come back on this one: a
  // column this sheet doesn't have reads as no sort (the tree), and its header shows no mark.
  const sort = pickedSort && (pickedSort.key === 'name' || columns.some((c) => c.id === pickedSort.key)) ? pickedSort : null;
  // Status / Comments are the only editable columns — matched by TITLE on whichever sheet is loaded
  // (so it works across seasons), mirroring what the server permits.
  const statusColId = useMemo(() => columns.find((c) => /^status$/i.test(c.title))?.id, [columns]);
  const commentsColId = useMemo(() => columns.find((c) => /^comments$/i.test(c.title))?.id, [columns]);
  // a boat row is the one carrying a SAP; its loading chip sits by its name
  const sapColId = useMemo(() => columns.find((c) => c.title === 'SAP')?.id, [columns]);
  const boatSap = (r: SheetGridRow) => {
    const sap = sapColId == null ? '' : cellStr(r.cells[sapColId]).trim();
    return /^\d{6,}$/.test(sap) ? sap : '';
  };
  const editable = (c: SheetGridColumn) => canWrite && (c.id === statusColId || c.id === commentsColId);

  // depth for indenting sub-rows (PDI sheets may or may not use Smartsheet's row hierarchy).
  const depthOf = useMemo(() => {
    const byId = new Map((grid.data?.rows ?? []).map((r) => [r.id, r]));
    const d = new Map<number, number>();
    const calc = (r: SheetGridRow): number => {
      if (d.has(r.id)) return d.get(r.id)!;
      const p = r.parentId != null ? byId.get(r.parentId) : undefined;
      const v = p ? calc(p) + 1 : 0;
      d.set(r.id, v);
      return v;
    };
    for (const r of grid.data?.rows ?? []) calc(r);
    return d;
  }, [grid.data]);

  // row hierarchy, so the full list can be browsed as a collapsible tree (drill in one boat at a time)
  // instead of a single 6000-row wall.
  const childrenOf = useMemo(() => {
    const m = new Map<number | null, SheetGridRow[]>();
    for (const r of grid.data?.rows ?? []) { const p = r.parentId ?? null; (m.get(p) ?? m.set(p, []).get(p)!).push(r); }
    return m;
  }, [grid.data]);
  const hasKids = (id: number) => (childrenOf.get(id)?.length ?? 0) > 0;
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const inited = useRef<string | null>(null);
  // the boat a link asked for: opened down to its items, marked and scrolled to, once
  const [focusRow, setFocusRow] = useState<number | null>(null);
  const focusDone = useRef(false);
  // when a sheet loads (or you switch sheet), open down to the boat level and collapse the checklists —
  // so you see a tidy list of boats and expand the one you want, like the Holds view.
  useEffect(() => {
    if (!grid.data || inited.current === active) return;
    inited.current = active;
    const open = new Set((grid.data.rows ?? []).filter((r) => (depthOf.get(r.id) ?? 0) <= 1).map((r) => r.id));
    if (focus && !focusDone.current && (!focus.sheetId || focus.sheetId === active)) {
      focusDone.current = true;
      const boat = grid.data.rows.find((r) => boatSap(r) === focus.sap);
      if (boat) {
        const byId = new Map(grid.data.rows.map((r) => [r.id, r]));
        for (let p = boat.parentId; p != null; p = byId.get(p)?.parentId ?? null) open.add(p);
        open.add(boat.id);
        for (const c of childrenOf.get(boat.id) ?? []) open.add(c.id);
        setFocusRow(boat.id);
      }
    }
    setOpenIds(open);
    // boatSap / childrenOf follow grid.data, which is already a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid.data, active, depthOf]);
  const toggleOpen = (id: number) => setOpenIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setAllOpen = (open: boolean) =>
    setOpenIds(open ? new Set((grid.data?.rows ?? []).map((r) => r.id)) : new Set());

  // status filter chips, from the values actually present in the mapped Status column.
  const statusValues = useMemo(() => {
    if (statusColId == null) return [];
    const set = new Set<string>();
    for (const r of grid.data?.rows ?? []) { const s = cellStr(r.cells[statusColId]).trim(); if (s) set.add(s); }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [grid.data, statusColId]);

  const rows = useMemo(() => {
    let all = grid.data?.rows ?? [];
    // accents, the Greek tonos and a final ς don't matter; a tick never matches the word "true"
    const query = fold(q.trim());
    if (query) all = all.filter((r) => Object.values(r.cells).some((c) => hasText(query, c)));
    if (statusSel !== 'ALL' && statusColId != null) all = all.filter((r) => cellStr(r.cells[statusColId]).trim() === statusSel);
    // the shared table sort: blanks sink either way, numbers and ticks as numbers, Greek + English text
    return sortRows(all, sort, (r, key) => (key === 'name' ? (primaryCol ? r.cells[primaryCol.id] : null) : r.cells[key]));
  }, [grid.data, q, statusSel, statusColId, sort, primaryCol]);

  // Tree mode (the default browse experience) shows the hierarchy collapsed so you drill boat-by-boat.
  // When you search, filter by status, or sort, we flatten to the matching rows instead.
  const treeMode = !q.trim() && statusSel === 'ALL' && sort == null;
  const visibleRows = useMemo(() => {
    if (!treeMode) return rows;
    const out: SheetGridRow[] = [];
    const walk = (parentId: number | null) => {
      for (const r of childrenOf.get(parentId) ?? []) {
        out.push(r);
        if ((childrenOf.get(r.id)?.length ?? 0) > 0 && openIds.has(r.id)) walk(r.id);
      }
    };
    walk(null);
    return out;
  }, [treeMode, rows, childrenOf, openIds]);

  // bring the focused boat into view once it's drawn. The name cell, not the whole row, so a wide sheet
  // never scrolls sideways to reach the row's far end.
  const scrolledTo = useRef<number | null>(null);
  useEffect(() => {
    if (focusRow == null || scrolledTo.current === focusRow) return;
    const id = requestAnimationFrame(() => {
      const cell = document.querySelector(`tr[data-row-id="${focusRow}"] > td`);
      if (cell) {
        cell.scrollIntoView({ block: 'center', inline: 'nearest' });
        scrolledTo.current = focusRow;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [focusRow, visibleRows]);

  // hide columns that are empty across the visible rows (unless "all columns"), but always keep the
  // editable Status/Comments columns — those are the point of the page.
  const shownCols = useMemo(() => {
    if (showAllCols) return otherCols;
    return otherCols.filter(
      (c) => c.id === statusColId || c.id === commentsColId ||
        rows.some((r) => { const x = r.cells[c.id]; return x === true || (typeof x === 'string' && x.trim() !== ''); }),
    );
  }, [otherCols, rows, showAllCols, statusColId, commentsColId]);
  // on a phone the two editable columns come straight after the name, so Status shows without a swipe.
  const gridCols = useMemo(() => {
    if (!isPhone) return shownCols;
    const lead = [statusColId, commentsColId].flatMap((id) => shownCols.filter((c) => c.id === id));
    return [...lead, ...shownCols.filter((c) => !lead.includes(c))];
  }, [isPhone, shownCols, statusColId, commentsColId]);

  const setCell = (rowId: number, col: SheetGridColumn, value: string | boolean | null) => write.mutate({ rowId, columnId: col.id, value });

  const editor = (col: SheetGridColumn, row: SheetGridRow) => {
    const val = row.cells[col.id];
    if (!editable(col)) {
      const s = col.type === 'DATE' || col.type === 'DATETIME' ? df.cell(col.type, val) : cellStr(val);
      const dot = col.symbol || col.id === statusColId ? statusDot(s) : null;
      if (dot) return <span className="svc-sheetcell"><span className="grp-dot" style={{ background: dot }} />{s}</span>;
      return <span style={{ whiteSpace: 'pre-wrap' }}>{s}</span>;
    }
    // Comments: always the append cell, never a dropdown that would hide (and overwrite) a dated note
    if (col.id === commentsColId)
      return <PdiCommentCell sheetId={active} rowId={row.id} col={col} text={cellStr(val)} label={cellStr(row.cells[primaryCol!.id])} />;
    if (col.options.length) {
      const dot = statusDot(cellStr(val));
      return (
        <span className="svc-sheetcell">
          {dot && <span className="grp-dot" style={{ background: dot }} />}
          <select className="field" aria-label={col.title} value={cellStr(val)} onChange={(e) => setCell(row.id, col, e.target.value || null)}>
            <option value="" />
            {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </span>
      );
    }
    const text = cellStr(val);
    // any other free text: write only on a real edit, compared as an <input> holds it (no line breaks)
    return (
      <input
        key={text}
        className="field"
        style={{ width: '100%' }}
        aria-label={col.title}
        defaultValue={text}
        onBlur={(e) => { if (e.target.value !== text.replace(/[\r\n]/g, '')) setCell(row.id, col, e.target.value); }}
      />
    );
  };
  // touch: a press anywhere in a branch's name cell (not on its own controls) opens / closes the branch
  const nameCellClick = (id: number) => (e: MouseEvent<HTMLElement>) => {
    if (!(e.target as HTMLElement).closest('button, a, input, select, textarea')) toggleOpen(id);
  };

  return (
    <>
      {sheets.data && sheets.data.sheets.length > 0 && (
        // phone: the search moves in here, so the bar folds to [search][Filters (n)] and the chips open below
        <FilterBar
          search={isPhone ? <SearchInput value={q} onChange={setQ} placeholder="Search the sheet…" width={210} /> : undefined}
          activeCount={statusSel !== 'ALL' ? 1 : 0}
        >
          <ChipGroup label="Sheet">
            {sheets.data.sheets.map((s) => (
              <Chip key={s.id} active={active === s.id} onClick={() => { setSheetId(s.id); setStatusSel('ALL'); setSort(null); }}>{s.label}</Chip>
            ))}
            {extraSheet && (
              <Chip active={active === extraSheet} onClick={() => { setSheetId(extraSheet); setStatusSel('ALL'); setSort(null); }}>{focus?.label || `Sheet ${extraSheet}`}</Chip>
            )}
          </ChipGroup>
          {statusValues.length > 0 && (
            <ChipGroup label="Status">
              <Chip active={statusSel === 'ALL'} onClick={() => setStatusSel('ALL')}>All</Chip>
              {statusValues.map((s) => (
                <Chip key={s} active={statusSel === s} dot={statusDot(s) ?? undefined} onClick={() => setStatusSel(s)}>{s}</Chip>
              ))}
            </ChipGroup>
          )}
          {treeMode && (
            <div className="svc-fgroup">
              <button className="svc-quick" onClick={() => setAllOpen(true)}>Expand all</button>
              <button className="svc-quick" onClick={() => setAllOpen(false)}>Collapse all</button>
            </div>
          )}
          <FilterToggle checked={showAllCols} onChange={setShowAllCols}>all columns</FilterToggle>
          <span style={{ flex: 1 }} />
          <span className="muted">{visibleRows.length} shown{treeMode ? '' : ` of ${rows.length}`} · {shownCols.length + 1} columns</span>
        </FilterBar>
      )}

      {(sheets.isLoading || grid.isLoading) && <LoadingState label="Loading PDI sheet…" />}
      {sheets.error && !sheets.data && <ErrorState error={sheets.error} onRetry={() => void sheets.refetch()} />}
      {sheets.error && sheets.data && <InlineError error={sheets.error} />}
      {grid.error && !grid.data && <ErrorState error={grid.error} onRetry={() => void grid.refetch()} />}
      {grid.error && grid.data && <InlineError error={grid.error} />}
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}

      {grid.data && primaryCol && (
        // is-grid + pin-col: on phones the header row and the name column both stick while Status / Comments scroll
        <div className="tbl-wrap is-grid">
          <table className="tbl pdi-fulltbl">
            <thead>
              <tr>
                <th {...sortTh('name', 'pin-col')}>{primaryCol.title}<SortMark dir={dirOf('name')} /></th>
                {gridCols.map((c) => (
                  <th key={c.id} {...sortTh(c.id)} title={c.title}>{c.title}{c.symbol ? ' •' : ''}<SortMark dir={dirOf(c.id)} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const depth = depthOf.get(r.id) ?? 0;
                const kids = hasKids(r.id);
                const tracker = cellStr(r.cells[primaryCol.id]);
                const sap = loadBySap ? boatSap(r) : '';
                return (
                  <tr key={r.id} data-row-id={r.id} className={[treeMode && kids ? 'pdi-branch' : '', r.id === focusRow ? 'is-focus' : ''].filter(Boolean).join(' ') || undefined}>
                    <td
                      className={`pdi-name-col pin-col${coarse && treeMode && kids ? ' pdi-name-tap' : ''}`}
                      style={{ paddingLeft: 8 + depth * (isPhone ? 8 : 16), fontWeight: depth <= 1 ? 700 : depth === 2 ? 600 : 400 }}
                      onClick={coarse && treeMode && kids ? nameCellClick(r.id) : undefined}
                    >
                      {/* a flex row on phones, so a wrapped name lines up with its first line, not under the twist */}
                      <span className="pdi-name-inner">
                        {treeMode && kids ? (
                          <button type="button" className={`pdi-twist${openIds.has(r.id) ? ' is-open' : ''}`} aria-expanded={openIds.has(r.id)} aria-label={`${openIds.has(r.id) ? 'Collapse' : 'Expand'} ${tracker || 'row'}`} onClick={() => toggleOpen(r.id)} title={openIds.has(r.id) ? 'Collapse' : 'Expand'}><IconChevronRight size={12} strokeWidth={2.2} /></button>
                        ) : treeMode ? <span className="pdi-twist-spacer" /> : null}
                        {/* the name, its file count and (on a boat) its loading chip flow as one text run,
                            so on a phone the chip wraps under the name inside the pinned column */}
                        <span className="pdi-name-txt">
                          {tracker || <span className="muted">-</span>}
                          {r.attachmentCount ? <span className="svc-meta" title={`${r.attachmentCount} file(s) in Smartsheet`}> <IconPaperclip size={12} /> {r.attachmentCount}</span> : null}
                          {sap && loadBySap && <LoadingChip sap={sap} p={loadBySap.get(sap) ?? NO_LOADING} />}
                        </span>
                      </span>
                    </td>
                    {gridCols.map((c) => {
                      const tint = r.fmt?.[c.id];
                      return (
                        <td
                          key={c.id}
                          className={editable(c) ? '' : 'muted'}
                          style={tint ? { background: tint, color: '#1a1a1a' } : undefined}
                          onPointerEnter={histEnter(r.id, c.id, c.title, cellStr(r.cells[primaryCol.id]))}
                          onPointerLeave={histLeave}
                          {...histPress(r.id, c.id, c.title, cellStr(r.cells[primaryCol.id]))}
                        >
                          {editor(c, r)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {visibleRows.length === 0 && (
                <tr><td colSpan={shownCols.length + 1} className="muted" style={{ textAlign: 'center', padding: 20 }}>No rows match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {histPopover}
    </>
  );
}

export function Pdi() {
  const { data, isLoading, error, refetch, dataUpdatedAt } = usePdi();
  const df = useDateFormatter();
  const [syncing, setSyncing] = useState(false);
  const { can } = useAuth();
  const canWrite = can('pdi:write');
  const [adding, setAdding] = useState(false); // the Add a boat dialog
  // each boat's loading progress, for people who can open the Loading page
  const canLoad = can('overview:read');
  const loading = useLoading(canLoad);
  const loadBySap = useMemo(() => (canLoad ? loadingProgressBySap(loading.data?.items ?? []) : null), [canLoad, loading.data]);
  const isPhone = useIsPhone();
  const [params] = useSearchParams();
  const focus = useMemo<PdiFocus | null>(() => {
    const sap = params.get('sap')?.trim();
    return sap ? { sap, sheetId: params.get('sheet') || null, label: params.get('label') || null } : null;
  }, [params]);
  const [q, setQ] = useState('');
  const [view, setView] = useState<View>(params.get('view') === 'full' ? 'full' : 'holds');
  const [sheet, setSheet] = useState('ALL');

  // share the page-level PDI controls (Holds/Full toggle, search, holds sheet filter) over co-op.
  useCoopView(
    { pdiTab: view, pdiQ: q, pdiHoldsSheet: sheet },
    (v) => {
      if (v.pdiTab === 'holds' || v.pdiTab === 'full') setView(v.pdiTab);
      if (typeof v.pdiQ === 'string') setQ(v.pdiQ);
      if (typeof v.pdiHoldsSheet === 'string') setSheet(v.pdiHoldsSheet);
    },
  );

  const sheets = useMemo(() => Array.from(new Set((data?.vessels ?? []).map((v) => v.sheet))), [data]);

  const [showPassed, setShowPassed] = useState(false);
  // the boats in view: the picked sheet and the search; on hold first, then (when asked) the passed ones
  const { onHold, passed } = useMemo(() => {
    let vessels = data?.vessels ?? [];
    if (sheet !== 'ALL') vessels = vessels.filter((v) => v.sheet === sheet);
    // accents, the Greek tonos and a final ς don't matter
    const query = fold(q.trim());
    if (query)
      vessels = vessels.filter(
        (v) =>
          hasText(query, v.sap, v.name, v.sheet) ||
          v.holds.some((h) => hasText(query, h.category, h.comment)) ||
          v.categories.some((c) => c.items.some((it) => !pdiChecked(it.status) && hasText(query, it.name, it.comment))),
      );
    return { onHold: vessels.filter(pdiOnHold), passed: vessels.filter(pdiPassed) };
  }, [data, q, sheet]);
  const rows = showPassed ? [...onHold, ...passed] : onHold;

  const totalHolds = useMemo(() => onHold.reduce((a, v) => a + v.holds.length, 0), [onHold]);
  const toCheck = useMemo(() => onHold.reduce((a, v) => { const p = pdiProgress(v); return a + p.total - p.checked; }, 0), [onHold]);
  const lastSync = dataUpdatedAt ? df.time(dataUpdatedAt) : null;

  return (
    <>
      <PageHeader title={view === 'holds' ? 'PDI - Holds' : 'PDI - Full list'}>
        {/* on a phone the full list carries its search in its own filter bar */}
        {!(isPhone && view === 'full') && <SearchInput value={q} onChange={setQ} placeholder={view === 'holds' ? 'Search boats, holds, items' : 'Search the sheet…'} width={210} />}
        {view === 'holds' && (
          // the list refreshes itself every 30s; "Syncing…" only answers a press, so it doesn't flicker
          <button className="btn btn-sm btn-primary pdi-sync" disabled={syncing} onClick={() => { setSyncing(true); void refetch().finally(() => setSyncing(false)); }}>
            <SyncLabel busy={syncing} />
          </button>
        )}
        {canWrite && <button className="btn btn-sm" onClick={() => setAdding(true)}>Add a boat</button>}
      </PageHeader>
      {adding && <PdiAddBoat onClose={() => setAdding(false)} />}

      <div className="toolbar" style={{ gap: 6 }}>
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="PDI view"
          options={[{ value: 'holds', label: 'Holds' }, { value: 'full', label: 'Full PDI list' }]}
        />
        {/* the switch already says it on a phone, so this caption drops there */}
        <span className="muted pdi-viewcap" style={{ fontSize: 11, marginLeft: 4 }}>
          {view === 'holds' ? 'the boats with something open' : 'the whole sheet, live from Smartsheet'}
        </span>
      </div>

      {view === 'full' ? (
        <FullList canWrite={canWrite} q={q} setQ={setQ} focus={focus} loadBySap={loadBySap} />
      ) : (
        <>
          {data && (data.vessels.length > 0) && (
            <>
              <FactSentence className="pdi-facts">
                {onHold.length > 0 ? (
                  <>
                    <Fact tone="warn">{onHold.length} boat{onHold.length === 1 ? '' : 's'}</Fact> on hold with{' '}
                    <Fact tone="warn">{totalHolds} hold{totalHolds === 1 ? '' : 's'}</Fact> and{' '}
                    <Fact>{toCheck} check{toCheck === 1 ? '' : 's'}</Fact> still to do<FactEnd>.</FactEnd>
                  </>
                ) : (
                  <>No boat is on hold.</>
                )}
                {passed.length > 0 && (
                  <>
                    {' '}<Fact
                      tone="ok"
                      onClick={() => setShowPassed((p) => !p)}
                      pressed={showPassed}
                      title={showPassed ? 'Hide the boats that have passed' : 'Show the boats that have passed too'}
                    >{passed.length} boat{passed.length === 1 ? '' : 's'}</Fact> passed<FactEnd>.</FactEnd>
                  </>
                )}
              </FactSentence>
              <FactSentence sub className="pdi-facts-sub">
                {lastSync ? `Synced ${lastSync}; it refreshes on its own. ` : ''}
                {canWrite ? 'A tap on Yes, No or Hold saves to Smartsheet straight away.' : ''}
              </FactSentence>
            </>
          )}
          <div className="toolbar pdi-sheetbar">
            <ChipGroup>
              <Chip dashed active={sheet === 'ALL'} onClick={() => setSheet('ALL')}>All sheets</Chip>
              {sheets.map((s) => (
                <Chip key={s} active={sheet === s} onClick={() => setSheet(s)}>{s}</Chip>
              ))}
            </ChipGroup>
          </div>

          {isLoading && <LoadingState label="Loading PDI…" />}
          {error && !data && <ErrorState error={error} onRetry={() => void refetch()} />}
          {error && data && <InlineError error={error} />}
          {data && rows.map((v) => (
            <VesselCard key={`${v.sheetId}-${v.rowId}`} v={v} canWrite={canWrite} load={loadBySap ? loadBySap.get(v.sap) ?? NO_LOADING : null} />
          ))}
          {data && rows.length === 0 && (
            <EmptyState
              title={!data.vessels.length ? 'No PDI data' : sheet === 'ALL' && !q.trim() ? 'No open holds' : 'No boats match'}
              hint={!data.vessels.length ? 'Press Sync to load PDI data.' : sheet === 'ALL' && !q.trim() ? 'No boat has an open hold.' : 'Try another sheet or search.'}
            />
          )}
        </>
      )}
    </>
  );
}
